// D-1026: SGP 要確認の取得と「確認済み」の記録。
//   判定は純関数(sgpReviewCheck.js)。ここは候補の行を読む・確認済みを読む / 書くだけ。
//   ★prize_orders は読むだけ。在庫(prize_stocks / stock_movements)には触れない。
import { supabase } from '../lib/supabase'
import { pendingSgpReviews, SGP_REVIEW_ARRIVED_FROM } from './sgpReviewCheck'

// 入荷チェック(useArrivalOrders)と同じ列 + order_date(年の推定に使う)。
export const SGP_REVIEW_COLS = [
  'prize_id', 'order_id', 'prize_name_short', 'prize_name_raw', 'supplier_id',
  'expected_date', 'case_count', 'received_quantity', 'is_fully_received',
  'pieces_per_case', 'unit_cost', 'case_cost', 'total_tax_included',
  'destination', 'status', 'arrived_at', 'unplanned_flag',
  'location_id', 'notes', 'order_source', 'order_date',
].join(', ')

// A の候補は直近 90 日の荷受けだけを読む(ただし RZ で荷受けが始まった 2026-07-01 より前は見ない)。
export const SGP_REVIEW_LOOKBACK_DAYS = 90
const MAX_ROWS = 500

function jstDaysAgo(days, now = new Date()) {
  const d = new Date(now.getTime() - days * 86400000)
  return d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

export function arrivedFromDate(now = new Date()) {
  const lookback = jstDaysAgo(SGP_REVIEW_LOOKBACK_DAYS, now)
  return lookback > SGP_REVIEW_ARRIVED_FROM ? lookback : SGP_REVIEW_ARRIVED_FROM
}

async function fetchCandidates(locationId, now) {
  let qa = supabase.from('prize_orders').select(SGP_REVIEW_COLS)
    .eq('supplier_id', 'SGP').eq('order_source', 'sgp_api')
    .eq('status', 'arrived')
    .gte('arrived_at', `${arrivedFromDate(now)}T00:00:00+09:00`)
    .order('arrived_at', { ascending: false })
    .limit(MAX_ROWS)
  let qb = supabase.from('prize_orders').select(SGP_REVIEW_COLS)
    .eq('supplier_id', 'SGP').eq('order_source', 'sgp_api')
    .in('status', ['ordered', 'shipped'])
    .not('is_fully_received', 'is', true)
    .or('notes.ilike.%納期遅延%,notes.ilike.%伝票変更%,notes.ilike.%納期変更%,notes.ilike.%納品延期%,notes.ilike.%出荷停止%')
    .order('order_date', { ascending: false })
    .limit(MAX_ROWS)
  if (locationId) {
    qa = qa.eq('location_id', locationId)
    qb = qb.eq('location_id', locationId)
  }
  const [ra, rb] = await Promise.all([qa, qb])
  if (ra.error) throw ra.error
  if (rb.error) throw rb.error
  // 2 本の問い合わせは status で分かれているので本来重ならないが、同じ行を 2 回出さない(画面の testid が重複する)。
  const seen = new Set()
  return [...(ra.data ?? []), ...(rb.data ?? [])].filter(o => o?.order_id && !seen.has(o.order_id) && seen.add(o.order_id))
}

// order_id → 確認済みの行の配列。テーブルがまだ無い(migration 未適用)・読めないときは {}(= 全部未確認として出す)。
export async function fetchConfirmations(orderIds) {
  if (!orderIds?.length) return {}
  const { data, error } = await supabase.from('sgp_review_confirmations')
    .select('order_id, kind, notes_key, confirmed_by, confirmed_at')
    .in('order_id', orderIds)
    .order('confirmed_at', { ascending: false })
    .limit(MAX_ROWS * 2)
  if (error) return {}
  const map = {}
  for (const r of data ?? []) (map[r.order_id] ??= []).push(r)
  return map
}

// まだ確認されていない要確認の一覧。取得に失敗したら ok=false・rows=[](誤った数を出さない)。
export async function fetchPendingSgpReviews({ locationId = null, now = new Date() } = {}) {
  try {
    const orders = await fetchCandidates(locationId, now)
    const confirmations = await fetchConfirmations(orders.map(o => o.order_id))
    return { ok: true, rows: pendingSgpReviews(orders, confirmations) }
  } catch {
    return { ok: false, rows: [] }
  }
}

// 「確認済み」を 1 行足す。成功で { ok: true }、失敗で { ok: false, message }。
export async function confirmSgpReview({ order, judgement, staffId }) {
  if (!order?.order_id || !judgement || !staffId) return { ok: false, message: '確認する人が分かりません' }
  const { error } = await supabase.from('sgp_review_confirmations').insert({
    order_id: order.order_id,
    kind: judgement.kind,
    notes_key: judgement.key,
    notes_snapshot: order.notes ?? null,
    confirmed_by: staffId,
  })
  if (error) return { ok: false, message: '確認済みにできませんでした' }
  return { ok: true }
}
